// Redux Articles module
// see https://github.com/erikras/ducks-modular-redux

import { pickUserIdFromAuthUser, readPersistedSessionIds } from '../../services/kronosPrefs/sessionIds';
import {
    persistTableSortStateToUserPrefs,
    readPersistedListTablesSortStateMap,
} from '../../services/kronosPrefs/listTablesSortState';

const UPDATE_TABLE_STATE = 'kronos/tables/UPDATE_TABLE_STATE';
const UPDATE_SHARED_LIST_PAGE_SIZE = 'kronos/tables/UPDATE_SHARED_LIST_PAGE_SIZE';

const DEFAULT_SHARED_LIST_PAGE_SIZE = 10;

const initialTableState = (overridePageSize) => {
    return {
        page: 0,
        pageSize: overridePageSize ?? 10,
        sortState: [],
        filters: []
    }
};

const PERSISTED_SORTS = readPersistedListTablesSortStateMap();

function tState (tableId, pageOverride) {
    const base = initialTableState(pageOverride);
    const s      = PERSISTED_SORTS[tableId];
    if (Array.isArray(s) && s.length) {
        return { ...base, sortState: s };
    }
    return base;
}

const initialState = {
    // Shared page size for the main list tables (customers, screenings,
    // programmations, tickets, engine logs, tasks...). The last pageSize
    // chosen by the user on any of these tables is stored here and reused
    // as the default for all of them.
    sharedListPageSize           : DEFAULT_SHARED_LIST_PAGE_SIZE,

    activitiesTable              : tState('activitiesTable'),
    articlesCategoriesTable      : tState('articlesCategoriesTable'),
    articlesTable                : tState('articlesTable'),
    cashregistersTable           : tState('cashregistersTable'),
    channelsTable                : tState('channelsTable'),
    customersTable               : tState('customersTable'),
    distributorsTable            : tState('distributorsTable'),
    eventsTable                  : tState('eventsTable'),
    eventsSearchTable            : tState('eventsSearchTable'),
    placesTable                  : tState('placesTable'),
    postCategoriesTable          : tState('postCategoriesTable'),
    postsTable                   : tState('postsTable'),
    pricingListsTable            : tState('pricingListsTable'),
    bucketListsTable             : tState('bucketListsTable'),
    bucketlistsBucketsTable      : tState('bucketlistsBucketsTable'),
    programmationsTable          : tState('programmationsTable'),
    resourcesTable               : tState('resourcesTable'),
    salepointsTable              : tState('salepointsTable'),
    screeningsTable              : tState('screeningsTable'),
    ticketsTable                 : tState('ticketsTable'),
    ticketsImportTable           : tState('ticketsImportTable', 100),
    sectionsTable                : tState('sectionsTable'),
    tasksTable                   : tState('tasksTable'),
    ticketTypesTable             : tState('ticketTypesTable'),
    tktInstancesTable            : tState('tktInstancesTable'),
    usersTable                   : tState('usersTable'),
    moviesStatsTable             : tState('moviesStatsTable'),
    pricesStatsTable             : tState('pricesStatsTable'),
    hourlyStatsTable             : tState('hourlyStatsTable'),
    dailyStatsTable              : tState('dailyStatsTable'),
    boxStatsTable                : tState('boxStatsTable'),
    roomsStatsTable              : tState('roomsStatsTable'),
    singleMovieDaysStatsTable    : tState('singleMovieDaysStatsTable'),
    singleMovieRevenueStatsTable : tState('singleMovieRevenueStatsTable'),
    singleMovieTicketsStatsTable : tState('singleMovieTicketsStatsTable'),
    tktUsersTable                : tState('tktUsersTable'),
    logTable                     : tState('logTable'),
    screeningTickets             : tState('screeningTickets'),
    phasesTable                  : tState('phasesTable'),
    periodsTable                 : tState('periodsTable'),
    sectorsTable                 : tState('sectorsTable'),
    applicationsTable            : tState('applicationsTable'),
    applicationTypesTable        : tState('applicationTypesTable'),
    workflowsTable               : tState('workflowsTable'),
    tickettypesWindowsTable      : tState('tickettypesWindowsTable', 100),
    tickettypesPricingsTable     : tState('tickettypesPricingsTable', 100),
    pricinglistsPricingsTable    : tState('pricinglistsPricingsTable', 100),
    cronTasksTable               : tState('cronTasksTable', 100),
    engineLogsTable              : tState('engineLogsTable'),
    emailCampaignsTable          : tState('emailCampaignsTable'),
    emailCampaignSendsTable      : tState('emailCampaignSendsTable'),
    bookingsAnalysisZip          : tState('bookingsAnalysisZip'),
    bookingsAnalysisCountry      : tState('bookingsAnalysisCountry'),
    bookingsAnalysisAge          : tState('bookingsAnalysisAge'),
    bookingsAnalysisSex          : tState('bookingsAnalysisSex'),
}

// Reducer
export default function reducer(state = initialState, action) {
    switch(action.type) {
        case UPDATE_TABLE_STATE:
            return {
                ...state,
                [action.id]: {
                    ...state[action.id],
                    ...action.state
                }
            };
        case UPDATE_SHARED_LIST_PAGE_SIZE:
            return {
                ...state,
                sharedListPageSize: action.pageSize
            };
        default:
            return state;
    }
};

export function updateTableState(id, state) { return { type: UPDATE_TABLE_STATE, id, state } }
export function updateSharedListPageSize(pageSize) { return { type: UPDATE_SHARED_LIST_PAGE_SIZE, pageSize } }

function resolvePrefsUserIdForTables (getState) {
    const fromStore = pickUserIdFromAuthUser(getState()?.auth?.user);
    if (fromStore) {
        return fromStore;
    }
    return readPersistedSessionIds().userId;
}

/** Persists list table sort state to `kronos.user.<id>`. */
export function persistListTablesSortMiddleware ({ getState }) {
    return (next) => (action) => {
        const out = next(action);
        if (action.type !== UPDATE_TABLE_STATE) {
            return out;
        }
        if (!Object.prototype.hasOwnProperty.call(action.state, 'sortState')) {
            return out;
        }
        const userId = resolvePrefsUserIdForTables(getState);
        persistTableSortStateToUserPrefs(userId, action.id, action.state.sortState);
        return out;
    };
}
